import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading, Overline } from "@/components/common/Primitives";
import { ExecutionTimeline, WhyAutomex, ContactBand } from "@/components/home/Sections";
import { useSeo } from "@/hooks/useSeo";
import { IMAGES, VALUES, ECOSYSTEM } from "@/data/site";
import { Target, Compass } from "lucide-react";

export default function About() {
  useSeo({
    title: "About AutoMex Group | Engineering the Next Generation of Industry",
    description: "AutoMex is an industrial platform combining infrastructure execution with industrial machinery and technology solutions for manufacturing in India.",
    path: "/about",
  });
  return (
    <>
      <PageHero
        overline="About AutoMex"
        title="Engineering the Next Generation of Industry."
        subtitle="AutoMex is being developed as an industrial platform combining infrastructure execution with industrial machinery and technology solutions."
        image={IMAGES.architecture}
        crumb="About"
      />

      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-slate-200">
              <img src={IMAGES.infra} alt="Modern industrial engineering facility" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>The Platform</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-automex-ink">One Master Brand. Two Verticals.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              AutoMex operates two connected verticals — <strong className="text-automex-blue">AutoMex Infra Projects</strong> for
              industrial infrastructure and turnkey execution, and <strong className="text-automex-blue">AutoMex Industrial Technologies</strong> for
              machinery, automation and material handling. Together they let a single partner plan, build, equip and commission a facility.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-5 border-t border-slate-200 pt-8">
              <img src="/logos/automex-infra-horizontal.png" alt="AutoMex Infra Projects" className="h-16 w-auto object-contain" />
              <img src="/logos/automex-tech.png" alt="AutoMex Industrial Technologies" className="h-16 w-auto object-contain" />
            </div>
            <div className="mt-8 flex flex-wrap gap-x-3 gap-y-2 text-sm font-semibold uppercase tracking-wide text-automex-blue">
              {ECOSYSTEM.map((s, i) => (
                <span key={s} className="flex items-center gap-3">{s}{i < ECOSYSTEM.length - 1 && <span className="text-automex-teal">→</span>}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-automex-mist bg-blueprint">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-6 md:grid-cols-2">
          {[
            { icon: Compass, tag: "Vision", text: "To become a trusted industrial partner for companies building and modernising the next generation of manufacturing in India." },
            { icon: Target, tag: "Mission", text: "To simplify industrial development through engineering coordination, reliable execution, industrial technology and integrated solutions." },
          ].map((b, i) => (
            <Reveal key={b.tag} delay={i * 0.1}>
              <div className="h-full bg-white border border-slate-200 rounded-sm p-8 sm:p-10">
                <span className="flex h-12 w-12 items-center justify-center bg-automex-navy text-automex-teal rounded-sm"><b.icon className="h-6 w-6" /></span>
                <h3 className="mt-6 font-heading text-2xl font-bold uppercase tracking-tight text-automex-ink">{b.tag}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><SectionHeading overline="Our Values" title="What We Engineer Around." /></Reveal>
          <div className="mt-14 grid gap-px sm:grid-cols-2 lg:grid-cols-3 bg-slate-200 border border-slate-200">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={(i % 3) * 0.05}>
                <div className="group flex items-center gap-4 bg-white p-8 transition-colors hover:bg-automex-navy">
                  <span className="font-heading text-3xl font-black text-automex-teal">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-heading text-lg font-bold uppercase tracking-tight text-automex-ink group-hover:text-white">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ExecutionTimeline />
      <WhyAutomex />
      <ContactBand />
    </>
  );
}
